import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {BarChart, ValueType} from 'charts';
import {
  getBarHeight,
  getGroupsForCompetitionRankings,
} from 'common/utils/chart-util';
import Card from 'common/components/card';
import CardCallouts from 'common/components/card-callouts';
import CardContent from 'common/components/card-content';
import CardHeader from 'common/components/card-header';
import SvgSymbol from 'common/components/svg-symbol';
import {formatUnits} from 'common/utils/units-util';
import {nt, t} from 'common/utils/translation-util';
import './index.scss';

const LEGEND_HEIGHT = 16;
const PERCENT = 'percentReduction';

export default class CompetitionRankingsCard extends React.Component {
  static propTypes = {
    connectDragSource: PropTypes.func,
    disableConfig: PropTypes.bool,
    report: PropTypes.object.isRequired,
  };

  static defaultProps = {
    connectDragSource: (x) => x,
    disableConfig: true,
  };

  getCountDown(competition) {
    const startDate = moment(competition.startDate);
    const now = moment().startOf('day');
    return startDate.diff(now, 'days');
  }

  getTrophyData(isPending, isPercent, zeroStateData, data) {
    if (isPending) {
      return zeroStateData;
    }
    return (
      Array.from(data)
        // It should not be necessary to sort competition rankings,
        // but there are conditions under which rankings do arrive
        // out of order (see BOS-19976). As a consequence, we
        // perform a prophylactic sort here just to make sure
        // that results line up visually.
        // This sort is also performed in getGroupsForCompetitionRankings().
        .sort((datum1, datum2) => {
          return isPercent
            ? datum2.percentReduction - datum1.percentReduction
            : datum2.totalReduction - datum1.totalReduction;
        })
    );
  }

  getZeroStateData(metric) {
    return Array.from({length: 5}, () => ({[metric]: null}));
  }

  getZeroStateSummary(data, competition, units) {
    let numParticipants = data.length;
    if (competition && competition.competitors) {
      numParticipants = competition.competitors.length;
    }

    return {
      dataSummary: [
        {
          label: t('Total savings'),
          units: formatUnits(units.shortName),
          value: '\u2014',
        },
        {
          isDifference: true,
          label: t('Difference'),
          units: t('%% reduced'),
          value: '\u2014',
        },
        {
          label: t('Participants'),
          units: t('buildings'),
          value: numParticipants,
        },
      ],
    };
  }

  render() {
    const {report} = this.props;
    const {competition, metric, show_values, subtitle, units} = report.meta;

    // out here to appease linter
    const competitionNotFound = !competition;
    let competitionHasNoParticipants = false;
    let competitionResultsCalculating = false;
    let countdown = 0;
    let groups = [];
    let isPending = false;
    let trophyData = [];
    let zeroStateSummary = null;

    const barHeight = getBarHeight(show_values);
    const trophies = ['trophy-gold', 'trophy-silver', 'trophy-bronze'];
    const zeroStateStyle = {textAlign: 'center'};

    if (competition) {
      countdown = this.getCountDown(competition);
      const isPercent = metric === 'percentReduction';

      // FIXME This needs to compare against an enum
      isPending = competition.status === 'pending';
      competitionResultsCalculating =
        !isPending && !report.data.length && competition.competitors.length;
      competitionHasNoParticipants =
        !isPending && !report.data.length && !competition.competitors.length;

      const zeroStateData = this.getZeroStateData(metric);
      zeroStateSummary = this.getZeroStateSummary(
        report.data,
        competition,
        units,
      );

      trophyData = this.getTrophyData(
        isPending,
        isPercent,
        zeroStateData,
        report.data,
      );

      groups = getGroupsForCompetitionRankings(report);
    }

    return (
      <Card className={'CompetitionRankingsCard'} loading={false}>
        {this.props.connectDragSource(
          <div>
            <CardHeader
              connectDragSource={this.props.connectDragSource}
              disableConfig={this.props.disableConfig}
              priorityWarnings={report.priorityWarnings}
              reportId={report.id}
              subtitle={subtitle}
              title={report.name || t('Unnamed')}
              warnings={report.warnings}
            />
          </div>,
        )}

        {competitionNotFound ? (
          <CardContent>
            <div style={zeroStateStyle}>
              {t('Competition is no longer available.')}
            </div>
          </CardContent>
        ) : null}

        {competitionResultsCalculating ? (
          <CardContent>
            <div style={zeroStateStyle}>
              {t('Competition results are calculating. Check back soon!')}
            </div>
          </CardContent>
        ) : null}

        {competitionHasNoParticipants ? (
          <CardContent>
            <div style={zeroStateStyle}>
              {t('This competition has no participants.')}
            </div>
          </CardContent>
        ) : null}

        {!competitionNotFound &&
        !competitionResultsCalculating &&
        !competitionHasNoParticipants ? (
          <CardContent>
            <CardCallouts meta={isPending ? zeroStateSummary : report.meta} />

            <div
              style={{
                marginTop: LEGEND_HEIGHT,
                position: 'relative',
              }}>
              <div
                className={classNames(
                  'CompetitionTrophies',
                  show_values ? '' : 'hidden-values',
                )}
                style={{
                  left: -4,
                  overflow: 'hidden',
                  width: 44,
                }}>
                {trophyData.map((datum, index) => {
                  const isNegative = datum.value < 0;
                  const trophy = !isNegative && trophies[index];
                  const ribbon = !isNegative && !trophies[index];
                  return (
                    <div
                      className={classNames(
                        'trophy',
                        isNegative ? 'trophy--negative' : '',
                      )}
                      key={`trophy-column-${index + 1}`}
                      style={{
                        lineHeight: `${barHeight}px`,
                        height: barHeight,
                      }}>
                      {!isNegative ? (
                        <SvgSymbol
                          className={classNames(
                            'medallion',
                            trophy ? 'medallion-cup' : '',
                            ribbon ? 'medallion-ribbon' : '',
                          )}
                          name={trophy || 'trophy-ribbon'}
                          useimg={true}
                        />
                      ) : null}

                      <span
                        className={classNames(
                          'placement-number',
                          trophy ? 'placement-number--cup' : '',
                          ribbon ? 'placement-number--ribbon' : '',
                        )}>
                        {index + 1}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  marginLeft: 55,
                }}>
                <BarChart
                  barHeight={barHeight}
                  groups={groups}
                  showAxis={true}
                  valueType={
                    metric === PERCENT
                      ? ValueType.PERCENTAGE
                      : ValueType.NUMERIC
                  }
                />
              </div>

              {isPending ? (
                <div
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    bottom: 0,
                    left: 0,
                    lineHeight: barHeight,
                    paddingTop: LEGEND_HEIGHT,
                    position: 'absolute',
                    right: 0,
                    textAlign: 'center',
                    top: 0,
                  }}>
                  {nt(
                    'This competition will begin in %(count)s day',
                    'This competition will begin in %(count)s days',
                    countdown,
                    {
                      count: countdown,
                    },
                  )}
                </div>
              ) : null}
            </div>
          </CardContent>
        ) : null}
      </Card>
    );
  }
}
